import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Divider,
  FormField,
  Group,
  Illustration,
  Link,
  SectionBannerCloseButton,
  SectionBannerContent,
  SectionBannerDescription,
  SectionBannerIcon,
  SectionBannerRoot,
  Text,
  VerificationCodeField,
} from '@melio/penny';

import { verifyMfa, challengeMfa, type AuthenticatorData } from '../../api/auth';
import { useAnalytics, type ErrorType } from '../../analytics';
import { getEntryPoint, getIdentificationMethod } from '../../session';
import { completeAuthRedirect } from '../../hooks/useRedirectParam';
import { clearStoredMfaData, readStoredMfaData } from '../../hooks/useMfaNavigation';
import { identifyUser } from '../../monitoring';
import { dismissDomainTransitionBanner } from '../DomainTransitionBanner/domainTransitionBannerCookie';
import { AuthenticatorSelectionModal } from './AuthenticatorSelectionModal';
import { config } from '../../config';

interface MfaData {
  mfaToken: string;
  authenticators: AuthenticatorData[];
  challengedAuthenticators: string[];
}

// Helper functions to handle different authenticator types (email vs SMS)
const getVerificationDestination = (selectedAuthenticator: AuthenticatorData) => {
  if (!selectedAuthenticator) return '';
  return selectedAuthenticator.description;
};

const getIllustrationType = (selectedAuthenticator: AuthenticatorData) => {
  if (!selectedAuthenticator) return 'new-email';
  switch (selectedAuthenticator.type) {
    case 'sms':
      return 'mobile';
    case 'email':
      return 'new-email';
    default: {
      const _exhaustiveCheck: never = selectedAuthenticator.type;
      return _exhaustiveCheck;
    }
  }
};

const getSubtitleText = (selectedAuthenticator: AuthenticatorData) => {
  if (!selectedAuthenticator) return '';
  switch (selectedAuthenticator.type) {
    case 'sms':
      return 'Enter the 6-digit verification code we sent to';
    case 'email':
      return 'We emailed your code to ';
    default: {
      const _exhaustiveCheck: never = selectedAuthenticator.type;
      return _exhaustiveCheck;
    }
  }
};

const getResendSuccessMessage = (selectedAuthenticator: AuthenticatorData) => {
  if (!selectedAuthenticator) return '';
  switch (selectedAuthenticator.type) {
    case 'sms':
      return 'We sent a new code to your phone.';
    case 'email':
      return 'We sent a new code to your email.';
    default: {
      const _exhaustiveCheck: never = selectedAuthenticator.type;
      return _exhaustiveCheck;
    }
  }
};

export const MFAChallengePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { track } = useAnalytics();

  const [error, setError] = useState<string | null>(null);
  const [tooManyCodeRequests, setTooManyCodeRequests] = useState(false);
  const [tooManyFailedAttempts, setTooManyFailedAttempts] = useState(false);
  const [sessionInvalid, setSessionInvalid] = useState(false);
  const [mfaData, setMfaData] = useState<MfaData | null>(null);
  const [selectedAuthenticator, setSelectedAuthenticator] = useState<AuthenticatorData | null>(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(config.resendMFACooldownSeconds);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [verificationKey, setVerificationKey] = useState(0);
  const [showMethodModal, setShowMethodModal] = useState(false);
  const [pendingAuthenticator, setPendingAuthenticator] = useState<AuthenticatorData | null>(null);
  const [isSwitchingMethod, setIsSwitchingMethod] = useState(false);
  const [remainingCodeVerificationAttempts, setRemainingCodeVerificationAttempts] = useState<number | undefined>(undefined);
  const [remainingCodeChallengeAttempts, setRemainingCodeChallengeAttempts] = useState<number | undefined>(undefined);
  const [codeExpiredBannerDismissed, setCodeExpiredBannerDismissed] = useState(false);
  const [isCodeInvalid, setIsCodeInvalid] = useState(false);
  const [isCodeLoading, setIsCodeLoading] = useState(false);

  // Track code interaction for analytics
  const codeInteracted = useRef(false);
  const viewTracked = useRef(false);
  const focusFirstCodeInput = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.getElementById('mfa-code-input-0')?.focus();
      });
    });
  };

  // Determine flow based on entry point
  const entryPoint = getEntryPoint();
  const flow = entryPoint === 'sign-up' ? 'sign-up' : 'sign-in';
  const intent = entryPoint === 'sign-up' ? 'sign-up' : 'sign-in';
  const pageName = entryPoint === 'sign-up' ? 'enter-your-verification-code' : 'verify-it-is-you';

  const trackView = () => {
    if (viewTracked.current) return;
    viewTracked.current = true;
    track({
      eventName: 'Connection-View',
      properties: {
        PageName: pageName,
        Flow: flow,
        Intent: intent,
        EntryPoint: entryPoint,
      },
    });
  };

  const trackStatus = (statusType: 'success' | 'failure', errorType?: ErrorType) => {
    track({
      eventName: 'Connection-Status',
      properties: {
        PageName: pageName,
        Flow: flow,
        Intent: intent,
        EntryPoint: entryPoint,
        IdentificationMethod: getIdentificationMethod(),
        StatusType: statusType,
        CodeInteraction: codeInteracted.current,
        ...(errorType && { ErrorType: errorType }),
      },
    });
  };

  // Countdown timer effect
  useEffect(() => {
    if (cooldownSeconds <= 0) return;

    const timer = setInterval(() => {
      setCooldownSeconds((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldownSeconds]);

  // This ensures refresh takes user back to the origin page instead of showing invalid MFA page
  useEffect(() => {
    const hasUnrecoverableError = tooManyFailedAttempts || tooManyCodeRequests || sessionInvalid || error;
    if (hasUnrecoverableError) {
      clearStoredMfaData();
    }
  }, [tooManyFailedAttempts, tooManyCodeRequests, sessionInvalid, error]);

  useEffect(() => {
    const stored = readStoredMfaData();

    if (!stored) {
      // Redirect to the correct page based on entry point from analytics session
      const redirectPath = entryPoint === 'sign-up' ? '/create-account' : '/sign-in';
      navigate({ pathname: redirectPath, search: location.search }, { replace: true });
      return;
    }

    const parsed = JSON.parse(stored) as MfaData;
    setMfaData(parsed);

    // Select first authenticator
    const defaultAuth = parsed.authenticators[0];
    setSelectedAuthenticator(defaultAuth || null);

    // Track page view
    trackView();
  }, [navigate, entryPoint]);

  const isLastVerificationAttempt = remainingCodeVerificationAttempts !== undefined && remainingCodeVerificationAttempts === 0;
  const isResendDisabled = remainingCodeChallengeAttempts !== undefined && remainingCodeChallengeAttempts === 0;

  // Helper to clear all error/success states before an action
  const clearStates = () => {
    setError(null);
    setTooManyCodeRequests(false);
    setTooManyFailedAttempts(false);
    setSessionInvalid(false);
    setResendSuccess(false);
  };

  // Shared handler for challengeMfa calls (resend code, switch method)
  const sendChallenge = async (authenticatorId: string, onSuccess: () => void) => {
    if (!mfaData) return;

    clearStates();

    const result = await challengeMfa({
      authenticatorId,
      mfaToken: mfaData.mfaToken,
    });

    switch (result.type) {
      case 'maxAttemptsReached':
        trackStatus('failure', 'too-many-resend');
        setTooManyCodeRequests(true);
        return;

      case 'sessionExpired':
        trackStatus('failure');
        setSessionInvalid(true);
        return;

      case 'error':
        trackStatus('failure');
        setError(result.message);
        return;

      case 'success':
        if (result.remainingCodeChallengeAttempts !== undefined) {
          setRemainingCodeChallengeAttempts(result.remainingCodeChallengeAttempts);
        }
        onSuccess();
        return;

      default: {
        const _exhaustiveCheck: never = result;
        return _exhaustiveCheck;
      }
    }
  };

  const handleCodeComplete = async (completedCode: string) => {
    if (!selectedAuthenticator || !mfaData) {
      setError('No authenticator selected');
      return;
    }

    // Mark code as interacted when user completes entering code
    codeInteracted.current = true;

    clearStates();
    setIsCodeInvalid(false);
    setIsCodeLoading(true);

    const result = await verifyMfa({
      code: completedCode,
      authenticatorId: selectedAuthenticator.id,
      mfaToken: mfaData.mfaToken,
    });

    switch (result.type) {
      case 'wrongCode':
        trackStatus('failure', 'invalid-code');
        if (result.remainingCodeVerificationAttempts !== undefined) {
          setRemainingCodeVerificationAttempts(result.remainingCodeVerificationAttempts);
        }
        if (result.remainingCodeChallengeAttempts !== undefined) {
          setRemainingCodeChallengeAttempts(result.remainingCodeChallengeAttempts);
        }
        setIsCodeInvalid(true);
        setIsCodeLoading(false);
        focusFirstCodeInput();
        return;

      case 'maxAttemptsReached':
        trackStatus('failure', 'too-many-failed-attempts');
        // Show "Too many failed attempts" banner and reset VerificationCodeField
        setTooManyFailedAttempts(true);
        setVerificationKey((prev) => prev + 1);
        setIsCodeLoading(false);
        return;

      case 'sessionExpired':
        trackStatus('failure');
        setSessionInvalid(true);
        setVerificationKey((prev) => prev + 1);
        setIsCodeLoading(false);
        return;

      case 'error':
        trackStatus('failure');
        setError(result.message);
        setIsCodeInvalid(true);
        setIsCodeLoading(false);
        return;

      case 'success':
        trackStatus('success');
        if (entryPoint === 'sign-up') {
          track({
            eventName: 'marketing_register_sign-up-with-email-password-success',
          });
        }
        // Clean up MFA data
        clearStoredMfaData();
        // Success - identify user and redirect
        identifyUser(result.user);
        dismissDomainTransitionBanner();
        completeAuthRedirect();
        setIsCodeLoading(false);
        return;

      default: {
        const _exhaustiveCheck: never = result;
        return _exhaustiveCheck;
      }
    }
  };

  const handleResendCode = async () => {
    if (!selectedAuthenticator || !mfaData || isResending) return;

    setIsResending(true);

    await sendChallenge(selectedAuthenticator.id, () => {
      setResendSuccess(true);
      setCooldownSeconds(config.resendMFACooldownSeconds);
      setRemainingCodeVerificationAttempts(undefined);
      setCodeExpiredBannerDismissed(false);
      setIsCodeInvalid(false);
      setIsCodeLoading(false);
      setVerificationKey((prev) => prev + 1);
      setTimeout(focusFirstCodeInput, 100);
    });

    setIsResending(false);
  };

  const handleContactSupport = (e: React.MouseEvent) => {
    e.preventDefault();
    window.location.href = 'mailto:support@melio.com';
  };

  const handleOpenMethodModal = (e: React.MouseEvent) => {
    e.preventDefault();
    setPendingAuthenticator(selectedAuthenticator);
    setShowMethodModal(true);
  };

  const handleCloseMethodModal = () => {
    setShowMethodModal(false);
    setPendingAuthenticator(null);
  };

  const handleSwitchMethod = async () => {
    if (!pendingAuthenticator || !mfaData || isSwitchingMethod) return;

    // If the pending authenticator is the same as the current, just close the modal
    if (pendingAuthenticator.id === selectedAuthenticator?.id) {
      handleCloseMethodModal();
      return;
    }

    setIsSwitchingMethod(true);

    await sendChallenge(pendingAuthenticator.id, () => {
      setSelectedAuthenticator(pendingAuthenticator);
      setResendSuccess(false);
      setCooldownSeconds(config.resendMFACooldownSeconds);
      setRemainingCodeVerificationAttempts(undefined);
      setIsCodeInvalid(false);
      setIsCodeLoading(false);
      setVerificationKey((prev) => prev + 1);
    });

    setIsSwitchingMethod(false);
    handleCloseMethodModal();
  };

  // Check if there are multiple authenticators available
  const hasMultipleAuthenticators = mfaData && mfaData.authenticators.length > 1;

  if (!selectedAuthenticator || !mfaData) {
    return null;
  }

  // Check if any error state is active (including no remaining verification/challenge attempts)
  const noRemainingAttempts = remainingCodeVerificationAttempts === 0 && remainingCodeChallengeAttempts === 0;
  const hasError = tooManyFailedAttempts || tooManyCodeRequests || sessionInvalid || error || noRemainingAttempts;

  // Full page error state for all errors
  if (hasError) {
    return (
      <Container overflow="visible" paddingY="xxl" style={{ width: '100%', maxWidth: '480px' }} data-testid="email-code-verification-page">
        <Group variant="vertical" spacing="l" alignItems="center">
          {/* Error Illustration */}
          <Illustration size="large" type="error" />

          {/* Header */}
          <Group variant="vertical" spacing="xs" alignItems="center">
            <Text textStyle="heading1Semi" color="semantic.text.primary" data-testid="error-title">
              Too many failed attempts
            </Text>
            <Text textStyle="body2" color="semantic.text.primary" style={{ textAlign: 'center' }}>
              We couldn&apos;t confirm your identity. Try again later.
            </Text>
          </Group>

          {/* Need help section */}
          <Group variant="vertical" spacing="s" alignItems="center" style={{ width: '100%' }}>
            <Divider />
            <Group variant="horizontal" spacing="xs" alignItems="center">
              <Text textStyle="body2" color="semantic.text.primary">
                Need help?
              </Text>
              <Link
                href="#"
                variant="standalone"
                size="medium"
                color="secondary"
                label="Contact support"
                onClick={handleContactSupport}
                data-testid="contact-support-link"
              />
            </Group>
          </Group>
        </Group>
      </Container>
    );
  }

  return (
    <Container overflow="visible" width="full" maxWidth="480px" paddingY="xxl" data-testid="email-code-verification-page">
      <Group variant="vertical" spacing="l" alignItems="center">
        {/* Error Banner - shown when code expired (last verification attempt) */}
        {isLastVerificationAttempt && !resendSuccess && !isResendDisabled && !codeExpiredBannerDismissed && (
          <SectionBannerRoot variant="critical" data-testid="code-expired-banner">
            <SectionBannerIcon />
            <SectionBannerContent>
              <Container
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <SectionBannerDescription>Code expired</SectionBannerDescription>
                <Link
                  href="#"
                  variant="standalone"
                  size="medium"
                  label="Resend code"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    setCodeExpiredBannerDismissed(true);
                    handleResendCode();
                  }}
                  data-testid="resend-code-link"
                />
              </Container>
            </SectionBannerContent>
          </SectionBannerRoot>
        )}

        {/* Success Banner - shown after code resend */}
        {resendSuccess && (
          <SectionBannerRoot variant="success" data-testid="resend-success-banner">
            <SectionBannerIcon />
            <SectionBannerContent>
              <SectionBannerDescription>{getResendSuccessMessage(selectedAuthenticator)}</SectionBannerDescription>
            </SectionBannerContent>
            <SectionBannerCloseButton onClick={() => setResendSuccess(false)} />
          </SectionBannerRoot>
        )}

        {/* Illustration */}
        <Illustration size="large" type={getIllustrationType(selectedAuthenticator)} />

        {/* Header */}
        <Group variant="vertical" spacing="xxl" alignItems="center">
          <Group variant="vertical" spacing="xs" alignItems="center">
            <Text textStyle="heading1Semi" color="semantic.text.primary" data-testid="verification-page-title">
              {entryPoint === 'sign-up' ? 'Enter your verification code' : "Verify it's you"}
            </Text>
            <Text textStyle="body2" color="semantic.text.primary" style={{ textAlign: 'center' }} data-testid="verification-page-subtitle">
              {getSubtitleText(selectedAuthenticator)}{' '}
              <Text as="span" textStyle="body2Semi" color="semantic.text.primary">
                {getVerificationDestination(selectedAuthenticator)}
              </Text>
              .
            </Text>
            {hasMultipleAuthenticators && (
              <Link
                href="#"
                variant="standalone"
                size="medium"
                color="secondary"
                label="Verify with another method"
                onClick={handleOpenMethodModal}
                data-testid="verify-another-method-link"
              />
            )}
          </Group>

          {/* Verification Code Input */}
          <Group variant="vertical" spacing="xs" alignItems="center">
            <FormField
              isDisabled={isLastVerificationAttempt}
              render={() => (
                <VerificationCodeField
                  key={`${selectedAuthenticator.id}-${verificationKey}`}
                  onComplete={handleCodeComplete}
                  isInvalid={isCodeInvalid}
                  isLoading={isCodeLoading}
                  length={6}
                  autoFocus={!isLastVerificationAttempt}
                  disabled={isLastVerificationAttempt}
                  invalidErrorMessages="Incorrect code."
                  idPrefix="mfa-code-input"
                  data-testid="verification-code-input"
                  data-private
                />
              )}
            />

            {/* Resend Code - only show when not disabled and when code-expired banner is not shown */}
            {!isResendDisabled && (!isLastVerificationAttempt || resendSuccess || codeExpiredBannerDismissed) && (
              <>
                {isResending ? (
                  <Text textStyle="body4" color="semantic.text.secondary" data-testid="sending-text">
                    Sending...
                  </Text>
                ) : cooldownSeconds > 0 ? (
                  <Text textStyle="body4" color="semantic.text.secondary" data-testid="cooldown-text">
                    Resend code in {cooldownSeconds} seconds
                  </Text>
                ) : (
                  <Link
                    href="#"
                    variant="standalone"
                    size="medium"
                    color="default"
                    label="Resend code"
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      handleResendCode();
                    }}
                    data-testid="resend-code-link"
                  />
                )}
              </>
            )}
          </Group>

          {/* Need help section */}
          <Group variant="vertical" spacing="s" alignItems="center" style={{ width: '100%' }}>
            <Divider />
            <Group variant="horizontal" spacing="xs" alignItems="center">
              <Text textStyle="body2" color="semantic.text.primary">
                Need help?
              </Text>
              <Link
                href="#"
                variant="standalone"
                size="medium"
                color="secondary"
                label="Contact support"
                onClick={handleContactSupport}
                data-testid="contact-support-link"
              />
            </Group>
          </Group>
        </Group>
      </Group>

      {/* Authenticator Selection Modal */}
      <AuthenticatorSelectionModal
        isOpen={showMethodModal}
        onClose={handleCloseMethodModal}
        onConfirm={handleSwitchMethod}
        isLoading={isSwitchingMethod}
        authenticators={mfaData?.authenticators ?? []}
        currentAuthenticatorId={selectedAuthenticator?.id ?? null}
        selectedAuthenticator={pendingAuthenticator}
        onSelectAuthenticator={setPendingAuthenticator}
      />
    </Container>
  );
};
